/**
 * Moderation — the SM-11 report queue (claim / resolve) and the SM-13d media
 * review queue, unchanged endpoints and semantics, now living as one console
 * section with Reports/Media sub-tabs. Client-asserted evidence renders with
 * the explicit unverified banner (ARCH §7) — never as captured proof.
 */
import { useState } from "react";
import { copy } from "@humble/shared";
import { Button, Card, Sheet, Skeleton, toast } from "../../design/index.js";
import { isApiError } from "../../lib/api.js";
import { rawApi } from "../safety/raw-api.js";
import { useLoad } from "./use-load.js";

const SURFACES = ["roulette", "media_upload", "super_reject", "deck", "calls", "purchase"] as const;
const ACTIONS = [
  "no_action",
  "warn",
  "content_removed",
  "feature_restricted",
  "account_banned",
  "account_device_banned",
] as const;

interface ReportRow {
  id: string;
  category: string;
  free_text: string | null;
  source: string;
  status: string;
  sla_due_at: string | null;
  sla_breached: boolean;
  subject_id: string;
  subject_name: string | null;
  subject_lifecycle: string;
  reporter_id: string | null;
  reporter_name: string | null;
  reporter_low_trust: boolean;
  created_at: string;
  snapshot: unknown;
}

function EvidenceViewer({ snapshot }: { snapshot: unknown }) {
  const snap = snapshot as { roulette_buffer?: Array<{ provenance?: string }> } | null;
  const hasClientAsserted = Array.isArray(snap?.roulette_buffer)
    ? snap.roulette_buffer.some((i) => i?.provenance === "client_asserted")
    : false;
  return (
    <div>
      {hasClientAsserted && (
        <p className="admin__warning">
          ⚠ Contains reporter-supplied items (client_asserted) — unverified, forgeable. Never treat
          as captured proof.
        </p>
      )}
      <pre className="admin__json">{JSON.stringify(snapshot, null, 2)}</pre>
    </div>
  );
}

function QueueLoading() {
  return (
    <div className="admin__list" aria-busy="true">
      <Skeleton height={96} radius={18} />
      <Skeleton height={96} radius={18} />
    </div>
  );
}

function QueueError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card padding="lg" className="admin__error">
      <p>{message}</p>
      <Button size="sm" variant="primary" onClick={onRetry}>
        Retry
      </Button>
    </Card>
  );
}

function ReportsTab() {
  const [status, setStatus] = useState<"" | "open" | "in_review" | "resolved">("");
  const { data: reports, loading, error, reload } = useLoad<ReportRow[]>(
    `/admin/reports${status ? `?status=${status}` : ""}`,
  );
  const [open, setOpen] = useState<ReportRow | null>(null);
  const [action, setAction] = useState<(typeof ACTIONS)[number]>("no_action");
  const [surface, setSurface] = useState<(typeof SURFACES)[number]>("roulette");
  const [days, setDays] = useState(7);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const claim = async (id: string) => {
    try {
      await rawApi("POST", `/admin/reports/${id}/claim`);
      reload();
    } catch (err) {
      toast(isApiError(err) ? err.message : "Claim failed", "error");
    }
  };

  const resolve = async () => {
    if (!open) return;
    setBusy(true);
    try {
      await rawApi("POST", `/admin/reports/${open.id}/resolve`, {
        action,
        ...(action === "feature_restricted"
          ? { surface, until: new Date(Date.now() + days * 86_400_000).toISOString() }
          : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast("Resolved.", "success");
      setOpen(null);
      reload();
    } catch (err) {
      toast(isApiError(err) ? err.message : "Resolve failed", "error");
    } finally {
      setBusy(false);
    }
  };

  if (error) return <QueueError message={error} onRetry={reload} />;
  if (loading) return <QueueLoading />;

  return (
    <div className="admin__list" data-testid="admin-reports">
      <div className="admin__filters">
        <label className="admin__filter">
          Status{" "}
          <select value={status} onChange={(e) => setStatus(e.target.value as never)}>
            <option value="">all</option>
            <option value="open">open</option>
            <option value="in_review">in review</option>
            <option value="resolved">resolved</option>
          </select>
        </label>
      </div>
      {(reports ?? []).map((r) => (
        <Card key={r.id} padding="md">
          <div className="admin__rowhead">
            <strong>{r.category}</strong>
            <span className="admin__meta">{r.source}</span>
            <span className="admin__time">{new Date(r.created_at).toLocaleString()}</span>
            <span className={`admin__pill admin__pill--${r.status}`}>{r.status}</span>
            {r.sla_breached && <span className="admin__pill admin__pill--sla">SLA BREACH</span>}
            {r.reporter_low_trust && (
              <span
                className="admin__pill admin__pill--lowtrust"
                title="High 24h filing volume — auto-sanction weight removed"
              >
                low-trust reporter
              </span>
            )}
          </div>
          <div className="admin__meta">
            Subject: {r.subject_name ?? r.subject_id} ({r.subject_lifecycle}) · Reporter:{" "}
            {r.reporter_name ?? r.reporter_id ?? "system"}
          </div>
          {r.free_text && <p style={{ margin: "6px 0" }}>{r.free_text}</p>}
          <div className="admin__actions">
            {r.status === "open" && (
              <Button size="sm" onClick={() => void claim(r.id)}>
                Claim
              </Button>
            )}
            {r.status !== "resolved" && (
              <Button size="sm" variant="primary" onClick={() => setOpen(r)}>
                Review & resolve
              </Button>
            )}
          </div>
        </Card>
      ))}
      {(reports ?? []).length === 0 && <p className="admin__empty">{copy.admin.queue_empty}</p>}

      <Sheet open={open !== null} onClose={() => setOpen(null)} title="Resolve report">
        {open && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <EvidenceViewer snapshot={open.snapshot} />
            <label>
              Action{" "}
              <select value={action} onChange={(e) => setAction(e.target.value as never)}>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            {action === "feature_restricted" && (
              <div style={{ display: "flex", gap: 8 }}>
                <select value={surface} onChange={(e) => setSurface(e.target.value as never)}>
                  {SURFACES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  style={{ width: 70 }}
                />
                <span className="text-dim">days</span>
              </div>
            )}
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Internal note / reason shown in warning copy"
            />
            <Button variant="danger" loading={busy} onClick={() => void resolve()}>
              Resolve
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  );
}

interface MediaRow {
  id: string;
  owner_id: string;
  owner_name: string | null;
  kind: string;
  status: string;
  mime: string;
  transcript: string | null;
  transcript_ok: boolean | null;
  scan_result: unknown;
  created_at: string;
}

function MediaTab() {
  const { data: rows, loading, error, reload } = useLoad<MediaRow[]>("/admin/media");
  const [busyId, setBusyId] = useState<string | null>(null);

  const act = async (id: string, action: "approve" | "remove") => {
    setBusyId(id);
    try {
      await rawApi("POST", `/admin/media/${id}/${action}`, action === "remove" ? { reason: "policy" } : undefined);
      reload();
    } catch (err) {
      toast(isApiError(err) ? err.message : "Action failed", "error");
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <QueueError message={error} onRetry={reload} />;
  if (loading) return <QueueLoading />;

  return (
    <div className="admin__list" data-testid="admin-media">
      {(rows ?? []).map((m) => (
        <Card key={m.id} padding="md">
          <div className="admin__rowhead">
            <strong>{m.kind}</strong>
            <span className="admin__pill">{m.status}</span>
            <span className="admin__meta">{m.mime}</span>
            <span className="admin__time">
              {m.owner_name ?? m.owner_id} · {new Date(m.created_at).toLocaleString()}
            </span>
          </div>
          {m.transcript !== null && (
            <p style={{ fontSize: "0.85rem" }}>
              Transcript{m.transcript_ok === false ? " (low confidence)" : ""}: {m.transcript}
            </p>
          )}
          {m.scan_result !== null && (
            <pre className="admin__json" style={{ maxHeight: 120 }}>
              {JSON.stringify(m.scan_result)}
            </pre>
          )}
          <div className="admin__actions">
            <Button size="sm" variant="primary" loading={busyId === m.id} onClick={() => void act(m.id, "approve")}>
              Approve → LIVE
            </Button>
            <Button size="sm" variant="danger" loading={busyId === m.id} onClick={() => void act(m.id, "remove")}>
              Remove (+strike)
            </Button>
          </div>
        </Card>
      ))}
      {(rows ?? []).length === 0 && <p className="admin__empty">{copy.admin.media_empty}</p>}
    </div>
  );
}

export function ModerationSection() {
  const [tab, setTab] = useState<"reports" | "media">("reports");
  return (
    <div>
      <div className="admin__tabs">
        {(["reports", "media"] as const).map((t) => (
          <Button key={t} variant={tab === t ? "primary" : "ghost"} size="sm" onClick={() => setTab(t)}>
            {t}
          </Button>
        ))}
      </div>
      {tab === "reports" ? <ReportsTab /> : <MediaTab />}
    </div>
  );
}
